'use client'

import { useQuery } from '@apollo/client/react'
import { useMemo } from 'react'

import { gql } from '../../../__generated__/graphql/catalog/gql'
import { useGraphqlClients } from '../../_providers/GraphqlClientsProvider.client'
import { useUser } from '../../_providers/UserProvider.client'

import { buildOwnedColorCounts } from './purchasedBadgeText'

const GET_OWNED_VARIANT_GROUPS_FOR_BADGE_QUERY = gql(`
  query GetOwnedVariantGroupsForBadge(
    $first: Int!
    $status: [OwnedProductStatus!]
    $productHandle: String
  ) {
    customerProfile {
      id
      ownedVariantGroups(first: $first, status: $status, productHandle: $productHandle) {
        edges {
          node {
            id
            color
            customerOrderInfo {
              summary {
                purchaseCount
              }
            }
          }
        }
      }
    }
  }
`)

const MAX_OWNED_VARIANT_GROUPS = 24

export const useOwnedColorCountsForProduct = (
  productHandle: string,
  { skip = false }: { skip?: boolean } = {}
): Record<string, number> | undefined => {
  const { user } = useUser()
  const { personalizedClient } = useGraphqlClients()

  const { data } = useQuery(GET_OWNED_VARIANT_GROUPS_FOR_BADGE_QUERY, {
    client: personalizedClient,
    variables: { first: MAX_OWNED_VARIANT_GROUPS, status: ['OWNED'], productHandle },
    skip: skip || !user,
    errorPolicy: 'all',
  })

  return useMemo(() => {
    const nodes = (data?.customerProfile?.ownedVariantGroups?.edges ?? []).flatMap(edge => {
      const node = edge?.node
      const purchaseCount = node?.customerOrderInfo?.summary?.purchaseCount
      if (!node?.color || purchaseCount == null) return []
      return [{ color: node.color, customerOrderInfo: { summary: { purchaseCount } } }]
    })
    if (nodes.length === 0) return undefined
    return buildOwnedColorCounts(nodes)
  }, [data])
}
